import React from "react";
import { createContext, useContext, ReactNode, useState } from "react";
import SuccessPopup from "../ui/Popup/SuccessPopup";
import ErrorPopup from "../ui/Popup/ErrorPopup";
import ConfirmationPopup from "../ui/Popup/ConfirmationPopup";
import { Snackbar, Alert } from "@mui/material";
import { AxiosError } from "axios";
import MainUseCase from "../../app/routing/domain/usecases/MainUseCase";
import MainApi from "../../app/routing/data/MainApi";

type PopupType = "success" | "error" | "snackbar" | "expired" | "confirmation";

type PopupContextValue = {
  showPopup: (
    type: PopupType,
    title: string,
    description: string,
    isRedirect?: boolean,
    imagePath?: string
  ) => void;
  showSnackbar: (desciprtion: string) => void;
  showConfirmation: (
    title: string,
    description: string,
    onConfirm: () => void
  ) => void;
  handleErrorResponse: (error: any) => void;
};

export const PopupContext = createContext<PopupContextValue>({
  showPopup: () => {},
  showConfirmation: () => {},
  showSnackbar: () => {},
  handleErrorResponse: () => {},
});

export function PopupProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<PopupType>("error");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isRedirect, setIsRedirect] = useState(false);
  const [image, setImage] = useState();
  const [onConfirmAction, setOnConfirmAction] = useState<(() => void) | null>(
    null
  );

  const fetchImage = new MainUseCase(new MainApi());
  const onFetchImage = async (logo: string) => {
    try {
      const response = await fetchImage.getImagePath(logo);
      return new Promise((resolve) => {
        setImage(response);
        resolve(response);
      });
    } catch (error: any) {
      throw error;
    } finally {
      setOpen(true);
    }
  };

  function showPopup(
    type: PopupType,
    title: string,
    description: string,
    isRedirect?: boolean,
    imagePath?: string
  ) {
    setType(type as PopupType);
    setTitle(title ? title : "");
    setDescription(description ? description : "General Error");
    setIsRedirect(isRedirect ? isRedirect : false);
    // setImagePath(imagePath ? imagePath : "");
    if (imagePath) onFetchImage(imagePath);
    else setOpen(true);
  }

  function showConfirmation(
    title: string,
    description: string,
    onConfirm: () => void
  ) {
    setOpen(true);
    setType("confirmation");
    setTitle(title);
    setDescription(description);
    setOnConfirmAction(() => onConfirm);
  }

  function showSnackbar(description: string) {
    setType("snackbar" as PopupType);
    setDescription(description ? description : "Success");
    setOpen(true);
  }
  function handleErrorResponse(error: any) {
    if (error && error instanceof AxiosError) {
      console.log("error", error);
      if (error.response !== undefined && error.response.data !== undefined) {
        if (
          error.response.status === 401 &&
          (error.response.data !== undefined ||
            error.response.data.rcdesc !== undefined ||
            error.response.data.rcDesc !== undefined ||
            error.response.data.responseDesc !== undefined)
        ) {
          const data = error.response.data;
          showPopup(
            "expired",
            data.responseTitle ? data.responseTitle : "",
            data instanceof String
              ? data
              : data.rcDesc
              ? data.rcDesc
              : data.responseDesc
              ? data.responseDesc
              : data.rcdesc
              ? data.rcdesc
              : "Token Expired",
            true,
            error.response.data.responseImage
          );
        } else if (
          error.response.status !== 401 &&
          (error.response.data !== undefined ||
            error.response.data.rcdesc !== undefined ||
            error.response.data.rcDesc !== undefined ||
            error.response.data.responseDesc !== undefined)
        ) {
          const data = error.response.data;
          showPopup(
            "error",
            data.responseTitle ? data.responseTitle : "",
            data instanceof String
              ? data
              : data.rcdesc
              ? data.rcdesc
              : data.responseDesc
              ? data.responseDesc
              : data.rcDesc
              ? data.rcDesc
              : "",
            false,
            error.response.data.responseImage
          );
        } else {
          showPopup("error", "", "");
        }
      } else {
        showPopup("error", "", "");
      }
    }
  }

  function handleClose() {
    setOpen(false);
  }

  return (
    <PopupContext.Provider
      value={{ showPopup, showSnackbar, showConfirmation, handleErrorResponse }}
    >
      {children}
      {type === "snackbar" ? (
        <>
          <Snackbar
            id="snackbar"
            open={open}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            onClose={handleClose}
          >
            <Alert
              id="alert-snackbar"
              onClose={handleClose}
              severity="success"
              variant="filled"
              sx={{ width: "100%" }}
            >
              {description}
            </Alert>
          </Snackbar>
        </>
      ) : type === "success" ? (
        <SuccessPopup
          display={open}
          onChangeDisplay={handleClose}
          description={description}
        />
      ) : type === "confirmation" ? (
        <ConfirmationPopup
          display={open}
          onChangeDisplay={handleClose}
          title={title}
          description={description}
          onConfirm={() => {
            if (onConfirmAction) onConfirmAction();
          }}
        />
      ) : (
        <ErrorPopup
          display={open}
          onChangeDisplay={handleClose}
          title={title}
          description={description}
          image={image}
          isRedirect={isRedirect}
          isExpired={type === "expired"}
        />
      )}
    </PopupContext.Provider>
  );
}

export function usePopup() {
  const { showPopup, showConfirmation, showSnackbar, handleErrorResponse } =
    useContext(PopupContext);
  return { showPopup, showConfirmation, showSnackbar, handleErrorResponse };
}
